import { getStore } from 'core_main/store';
import { handleError, LogLabel, debug } from 'globals/errors';
import { getConversationPubNub, reconnect } from './instance';
import { createConversationChannelGroupString } from './channelName';
// Message recovery is lazy-loaded to reduce initial bundle size
// It's only needed after successful reconnection

// Diagnostic log to verify module is loaded
debug({ message: 'checkAndReconnect.ts module loaded', labels: [LogLabel.PUBNUB_RECOVERY] });

/**
 * Default retry delay in milliseconds when reconnection check encounters an error.
 * Matches iOS/Android pattern: 5 seconds for error retries.
 */
const RECONNECTION_CHECK_ERROR_RETRY_DELAY_MS = 5000; // 5 seconds

/**
 * Network restoration delay in milliseconds.
 * Used when network becomes available to allow network stabilization before checking PubNub.
 * Matches Android pattern: 5 second delay for network restoration.
 */
const NETWORK_RECONNECTION_DELAY_MS = 5000; // 5 seconds

/**
 * Timeout for verifying subscriptions are active.
 * If listChannels doesn't respond within this time, assume subscriptions are inactive.
 */
const VERIFY_SUBSCRIPTIONS_TIMEOUT_MS = 5000; // 5 seconds

/**
 * Maximum number of retry attempts for reconnection checks.
 * Prevents infinite retry loops if PubNub is permanently unavailable.
 */
const MAX_RETRY_COUNT = 10;

/**
 * Interface for PubNub service operations needed for health checks and reconnection.
 * This interface enables dependency injection for easier testing.
 */
export interface PubNubService {
  verifySubscriptionsActive(): Promise<boolean>;
  reconnect(): Promise<void>;
  isPubNubConnected(): boolean;
}

/**
 * Timer functions for dependency injection (enables mocking in tests).
 */
export interface TimerFunctions {
  setTimeout: (callback: () => void, delay: number) => NodeJS.Timeout | number;
  clearTimeout: (timerId: NodeJS.Timeout | number) => void;
}

/**
 * Module-scoped flag to prevent overlapping calls to checkAndReconnectIfNeeded.
 * Only one check can run at a time.
 */
let isCheckingOrReconnecting = false;

/**
 * Module-scoped flag to prevent overlapping message recovery operations.
 * Only one recovery can run at a time.
 */
let isRecoveringMessages = false;

/**
 * Module-scoped flag to track if PubNub has been initialized at least once.
 * Used to skip message recovery on the very first initialization (not a recovery scenario).
 */
let hasInitializedPubNub = false;

/**
 * Module-scoped variable to track pending retry timer.
 * Used to cancel pending retries when a new check starts.
 */
let pendingRetryTimer: NodeJS.Timeout | number | undefined;

/**
 * Module-scoped retry counter to prevent infinite retry loops.
 * Reset to 0 when a check succeeds or when a new check starts (not from a retry).
 */
let retryCount = 0;

/**
 * Tracks whether the current call is from a retry timer.
 * Used to determine if we should reset retryCount at the start of a check.
 */
let isRetryCall = false;

/**
 * Verifies that PubNub subscriptions are active by calling listChannels API.
 * This is a proxy test that covers all health check cases:
 * - Not initialized (returns false)
 * - No connection (returns false)
 * - Inactive subscriptions (returns false)
 * - Healthy (returns true)
 *
 * Note: listChannels only calls the callback if error === false, so if the callback
 * is not called or is called with an error, subscriptions are not active.
 *
 * @param customerId Customer ID for channel group lookup
 * @param orgId Organization ID for channel group lookup
 * @returns Promise that resolves to true if subscriptions are active, false otherwise
 */
async function verifySubscriptionsActive(
  customerId: string,
  orgId: string,
): Promise<boolean> {
  try {
    const conversationPubNub = getConversationPubNub();
    if (!conversationPubNub?.instance) {
      return false;
    }

    const channelGroupString = createConversationChannelGroupString(
      customerId,
      orgId,
    );

    const listChannelsOperation = new Promise<boolean>((resolve) => {
      // Use global.setTimeout to ensure it's available in all environments
      const timeoutFn = typeof setTimeout !== 'undefined' ? setTimeout : global.setTimeout;
      const clearTimeoutFn = typeof clearTimeout !== 'undefined' ? clearTimeout : global.clearTimeout;
      
      const timeoutId = timeoutFn(() => {
        resolve(false);
      }, VERIFY_SUBSCRIPTIONS_TIMEOUT_MS);

      const result = conversationPubNub.listChannels(
        {
          channelGroup: channelGroupString,
        },
        (response) => {
          clearTimeoutFn(timeoutId);
          // Callback is only called if error === false (see instance.ts)
          // If we get a response, subscriptions are active
          resolve(!!response);
        },
      );

      // If listChannels returns undefined, there's no instance
      if (result === undefined) {
        clearTimeoutFn(timeoutId);
        resolve(false);
      }
    });

    return await listChannelsOperation;
  } catch (error) {
    return false;
  }
}

/**
 * Real PubNub service implementation that wraps the actual PubNub operations.
 */
class RealPubNubService implements PubNubService {
  async verifySubscriptionsActive(): Promise<boolean> {
    const store = getStore();
    const { customerId, orgId } = store;

    if (!customerId || !orgId) {
      return false;
    }

    return verifySubscriptionsActive(customerId, orgId);
  }

  async reconnect(): Promise<void> {
    reconnect();
    // PubNub's reconnect() is synchronous but triggers async reconnection.
    // Add a small delay to allow reconnection to start before verification.
    // The subsequent verifySubscriptionsActive() call will wait for subscriptions to be active.
    return new Promise<void>((resolve) => {
      const timeoutFn = typeof setTimeout !== 'undefined' ? setTimeout : global.setTimeout;
      timeoutFn(() => resolve(), 100);
    });
  }

  isPubNubConnected(): boolean {
    return getStore().isPubNubConnected;
  }
}

/**
 * After successful reconnection (or when subscriptions are verified as active),
 * fetch and process any undelivered messages that arrived while disconnected.
 * This ensures listeners are notified about recovered messages.
 * Skip recovery on the very first initialization (not a recovery scenario)
 * Only run recovery if PubNub has been initialized before (this is a reconnection)
 */
async function runRecoveryIfNeeded(realPubNubService: RealPubNubService): Promise<void> {
  // After successful reconnection (or when subscriptions are verified as active),
  // fetch and process any undelivered messages that arrived while disconnected.
  // This ensures listeners are notified about recovered messages.
  // Skip recovery on the very first initialization (not a recovery scenario)
  // Only run recovery if PubNub has been initialized before (this is a reconnection)
  // Check if PubNub is actually connected to avoid race condition where event fires
  // before initialization completes but after listeners are registered
  const store = getStore();
  if (!hasInitializedPubNub) {
    // Only mark as initialized if PubNub is actually connected AND subscriptions are active.
    // This ensures we only mark as initialized when PubNub is fully operational,
    // not just when isPubNubConnected is true (which can happen during async initialization).
    // This prevents the bug where a premature event during initialization prevents
    // the flag from being set, causing subsequent reconnections to be treated as first initialization.
    if (store.isPubNubConnected) {
      // Verify subscriptions are actually active before marking as initialized.
      // This distinguishes between "connected but not fully initialized" and "fully initialized".
      const subscriptionsActive = await realPubNubService.verifySubscriptionsActive();
      if (subscriptionsActive) {
        hasInitializedPubNub = true;
        debug({ message: 'Skipping message recovery on first initialization', labels: [LogLabel.PUBNUB_RECOVERY] });
        return;
      }
      // PubNub is connected but subscriptions are not active yet - still initializing.
      // Don't set the flag yet, let a future call when fully initialized set it.
      debug({ message: 'Skipping recovery - PubNub connected but subscriptions not yet active', labels: [LogLabel.PUBNUB_RECOVERY] });
      return;
    }
    // PubNub not connected yet, this is likely a premature event call
    // Don't set the flag yet, let the actual initialization set it
    debug({ message: 'Skipping recovery - PubNub not yet initialized', labels: [LogLabel.PUBNUB_RECOVERY] });
    return;
  }

  // Prevent overlapping recovery operations - only run if not already recovering
  if (!isRecoveringMessages) {
    isRecoveringMessages = true;
    try {
      debug({ message: 'Starting message recovery', labels: [LogLabel.PUBNUB_RECOVERY] });
      // Lazy-load message recovery function to reduce initial bundle size
      // Only load when actually needed (after successful reconnection)
      const { markUndeliveredMessagesAsDelivered } = await import(
        /* webpackChunkName: "pubnub-message-recovery" */ './markUndeliveredMessages'
      );
      const replayedCount = await markUndeliveredMessagesAsDelivered(true); // Pass recovery flag
      debug({
        message: `Recovery completed: ${replayedCount} message(s) replayed through listeners`,
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
    } catch (error) {
      handleError(error, {
        message: 'Failed to recover undelivered messages after reconnection',
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
    } finally {
      isRecoveringMessages = false;
    }
  } else {
    debug({ message: 'Message recovery already in progress, skipping', labels: [LogLabel.PUBNUB_RECOVERY] });
  }
}

/**
 * Checks PubNub connection and subscription health, reconnecting if needed.
 * This method should be called when a browser tab becomes visible after being
 * hidden for an extended period to ensure subscriptions are active.
 *
 * Uses a simplified implementation approach:
 * - Always calls verifySubscriptionsActive() (single source of truth)
 * - If verification succeeds → done (return success)
 * - If verification fails → reconnect/initialize
 *
 * @param pubnub The PubNub service instance (or mock) that conforms to PubNubService
 * @param timerFunctions Optional timer functions for dependency injection (defaults to global setTimeout/clearTimeout)
 * @param retryDelay Optional delay in milliseconds for scheduling retries (defaults to RECONNECTION_CHECK_ERROR_RETRY_DELAY_MS)
 * @returns Promise that resolves when the check/reconnection completes
 */
export async function checkAndReconnectIfNeeded(
  pubnub: PubNubService,
  timerFunctions?: TimerFunctions,
  retryDelay?: number,
): Promise<boolean> {
  const setTimeoutFn = timerFunctions?.setTimeout || global.setTimeout;
  const clearTimeoutFn = timerFunctions?.clearTimeout || global.clearTimeout;
  const delay = retryDelay ?? RECONNECTION_CHECK_ERROR_RETRY_DELAY_MS;

  // Cancel any pending retry timer before starting a new check
  if (pendingRetryTimer !== undefined) {
    clearTimeoutFn(pendingRetryTimer);
    pendingRetryTimer = undefined;
  }

  // Prevent overlapping calls - only one check can run at a time
  // Set flag immediately after check to minimize race condition window
  // (retry count logic can happen after flag is set since it doesn't affect overlap prevention)
  if (isCheckingOrReconnecting) {
    return false;
  }
  isCheckingOrReconnecting = true;

  // Reset retry count for new checks (not from retry callbacks)
  // This ensures each new health check attempt gets a fresh retry count
  if (!isRetryCall) {
    retryCount = 0;
  }
  isRetryCall = false; // Reset flag for next call

  // Declare retry callback before both blocks to eliminate duplication
  const retryCallback = async () => {
    pendingRetryTimer = undefined;
    isRetryCall = true; // Mark as retry call
    try {
      const recovered = await checkAndReconnectIfNeeded(pubnub, timerFunctions, delay);
      // If retry succeeded and we're using the real PubNub service, trigger recovery
      if (recovered && pubnub instanceof RealPubNubService) {
        await runRecoveryIfNeeded(pubnub);
      }
    } catch (error) {
      // Log error for debugging/monitoring
      handleError(error, { message: 'Health check retry failed', labels: [LogLabel.PUBNUB_RECOVERY] });
    }
  };

  // Common retry scheduling logic
  const scheduleRetryIfNeeded = () => {
    if (retryCount < MAX_RETRY_COUNT) {
      retryCount++;
      pendingRetryTimer = setTimeoutFn(retryCallback, delay);
    } else {
      // Max retries reached, reset counter and log debug message
      debug({
        message: `PubNub health check failed after ${MAX_RETRY_COUNT} retries. Resetting retry counter.`,
        labels: [LogLabel.PUBNUB_RECOVERY],
      });
      retryCount = 0;
    }
  };

  try {
    const store = getStore();
    const { customerId, orgId } = store;

    // Early exit if missing required data
    if (!customerId || !orgId) {
      isCheckingOrReconnecting = false;
      return false;
    }

    // Simplified implementation: Always verify subscriptions
    const isHealthy = await pubnub.verifySubscriptionsActive();

    if (isHealthy) {
      // Subscriptions are active, we're done
      // Reset retry count on success
      retryCount = 0;
      isCheckingOrReconnecting = false;
      return true;
    }

    // Subscriptions are not active, need to reconnect
    // If already connected, reconnect. If not connected, initialization will happen
    // elsewhere (e.g., when operations require PubNub)
    if (pubnub.isPubNubConnected()) {
      await pubnub.reconnect();
    }
    // If not connected, we can't initialize here due to circular dependency.
    // Initialization will happen when needed (e.g., getMessages, etc.)

    // Verify again after reconnection
    const isHealthyAfterReconnect = await pubnub.verifySubscriptionsActive();

    if (!isHealthyAfterReconnect) {
      // Reconnection failed, schedule retry if under max retry limit
      scheduleRetryIfNeeded();
      isCheckingOrReconnecting = false;
      return false;
    } 
      // Success after reconnection, reset retry count
      retryCount = 0;
    

    isCheckingOrReconnecting = false;
    return true;
  } catch (error) {
    // Any error encountered - schedule retry if under max retry limit
    isCheckingOrReconnecting = false;
    scheduleRetryIfNeeded();
    return false;
  }
}

/**
 * Wrapper function that provides the real PubNub service implementation.
 * This is the function that should be called from the rest of the codebase.
 *
 * After successful reconnection (or when subscriptions are verified as active),
 * this function fetches and processes any undelivered messages that arrived while
 * disconnected, ensuring listeners are notified about recovered messages.
 *
 * @returns Promise that resolves when the check/reconnection completes
 */
export async function checkAndReconnectIfNeededWrapper(): Promise<void> {
  debug({ message: 'checkAndReconnectIfNeededWrapper called', labels: [LogLabel.PUBNUB_RECOVERY] });

  const realPubNubService = new RealPubNubService();
  let isHealthy = false;
  try {
    isHealthy = await checkAndReconnectIfNeeded(realPubNubService);
  } catch (error) {
    handleError(error, { message: 'Error in checkAndReconnectIfNeededWrapper', labels: [LogLabel.PUBNUB_RECOVERY] });
    return;
  }

  // Only proceed with message recovery if health check succeeded
  // If health check failed or only scheduled a retry, skip recovery
  if (!isHealthy) {
    return;
  }

  await runRecoveryIfNeeded(realPubNubService);
}

/**
 * Resets all module-scoped state variables.
 * This should be called when the SDK is stopped to ensure clean state
 * for subsequent re-initialization.
 */
export function resetCheckAndReconnectState(): void {
  // Clear any pending retry timer
  if (pendingRetryTimer !== undefined) {
    // Use global clearTimeout to work with both real and fake timers
    if (typeof clearTimeout !== 'undefined') {
      clearTimeout(pendingRetryTimer as NodeJS.Timeout);
    }
    pendingRetryTimer = undefined;
  }

  // Reset all state flags
  isCheckingOrReconnecting = false;
  isRecoveringMessages = false;
  hasInitializedPubNub = false;
  retryCount = 0;
  isRetryCall = false;

  debug({ message: 'checkAndReconnect state reset', labels: [LogLabel.PUBNUB_RECOVERY] });
}

/**
 * Sets the hasInitializedPubNub flag for testing purposes.
 * This allows tests to simulate the state after first initialization.
 */
export function setHasInitializedPubNub(value: boolean): void {
  hasInitializedPubNub = value;
}

// Export constants for use in other modules
export {
  RECONNECTION_CHECK_ERROR_RETRY_DELAY_MS,
  NETWORK_RECONNECTION_DELAY_MS,
  VERIFY_SUBSCRIPTIONS_TIMEOUT_MS,
  MAX_RETRY_COUNT,
};

// Module-scoped variables to track cleanup functions and network reconnect timer for monitoring
let visibilityCleanup: (() => void) | null = null;
let networkCleanup: (() => void) | null = null;
let networkReconnectTimer: NodeJS.Timeout | null = null;

/**
 * Sets up Page Visibility API monitoring to check PubNub connection health
 * when the browser tab becomes visible.
 *
 * @returns Cleanup function to remove event listeners
 */
function setupVisibilityMonitoring(): () => void {
  debug({ message: 'setupVisibilityMonitoring called', labels: [LogLabel.PUBNUB_RECOVERY] });
  const handleVisibilityChange = () => {
    debug({
      message: `visibilitychange event fired, visibilityState: ${document.visibilityState}`,
      labels: [LogLabel.PUBNUB_RECOVERY]
    });
    if (document.visibilityState === 'visible') {
      // Tab became visible - check PubNub connection health
      debug({ message: 'Visibility changed to visible, calling checkAndReconnectIfNeededWrapper', labels: [LogLabel.PUBNUB_RECOVERY] });
      // Errors are handled inside checkAndReconnectIfNeededWrapper, no need for additional error handling
      checkAndReconnectIfNeededWrapper();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  debug({ message: 'visibilitychange listener added', labels: [LogLabel.PUBNUB_RECOVERY] });

  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * Sets up network connectivity monitoring to check PubNub connection health
 * when network becomes available.
 *
 * @returns Cleanup function to remove event listeners
 */
function setupNetworkMonitoring(): () => void {
  debug({ message: 'setupNetworkMonitoring called', labels: [LogLabel.PUBNUB_RECOVERY] });
  const handleNetworkOnline = () => {
    // Cancel any pending network reconnect timer
    if (networkReconnectTimer) {
      clearTimeout(networkReconnectTimer);
      networkReconnectTimer = null;
    }

    // Network became available - schedule PubNub health check after delay for network stabilization
    // Use NETWORK_RECONNECTION_DELAY_MS constant (matches Android pattern: 5 second delay)
    debug({ message: 'Network came online, scheduling checkAndReconnectIfNeededWrapper', labels: [LogLabel.PUBNUB_RECOVERY] });
    networkReconnectTimer = setTimeout(() => {
      networkReconnectTimer = null;
      debug({ message: 'Network online timer fired, calling checkAndReconnectIfNeededWrapper', labels: [LogLabel.PUBNUB_RECOVERY] });
      // Errors are handled inside checkAndReconnectIfNeededWrapper, no need for additional error handling
      checkAndReconnectIfNeededWrapper();
    }, NETWORK_RECONNECTION_DELAY_MS);
  };

  window.addEventListener('online', handleNetworkOnline);
  debug({ message: 'online event listener added', labels: [LogLabel.PUBNUB_RECOVERY] });

  // Return cleanup function
  return () => {
    if (networkReconnectTimer) {
      clearTimeout(networkReconnectTimer);
      networkReconnectTimer = null;
    }
    window.removeEventListener('online', handleNetworkOnline);
  };
}

/**
 * Sets up visibility and network monitoring for PubNub health checks.
 * This function is lazy-loaded to reduce initial bundle size.
 * Should be called during SDK initialization.
 */
export function setupHealthCheckMonitoring(): void {
  debug({ message: 'Setting up visibility and network monitoring', labels: [LogLabel.PUBNUB_RECOVERY] });
  
  // Clean up any existing event listeners before setting up new ones
  if (visibilityCleanup) {
    visibilityCleanup();
    visibilityCleanup = null;
  }
  if (networkCleanup) {
    networkCleanup();
    networkCleanup = null;
  }

  // Set up visibility and network monitoring
  visibilityCleanup = setupVisibilityMonitoring();
  networkCleanup = setupNetworkMonitoring();
  debug({ message: 'Monitoring setup complete', labels: [LogLabel.PUBNUB_RECOVERY] });
}

/**
 * Cleans up event listeners and timers set up by setupHealthCheckMonitoring().
 * This should be called when the SDK is stopped to prevent memory leaks.
 */
export function cleanupHealthCheckMonitoring(): void {
  // Clean up visibility monitoring
  if (visibilityCleanup) {
    visibilityCleanup();
    visibilityCleanup = null;
  }
  // Clean up network monitoring
  if (networkCleanup) {
    networkCleanup();
    networkCleanup = null;
  }
  // Clean up any pending network reconnect timer
  if (networkReconnectTimer) {
    clearTimeout(networkReconnectTimer);
    networkReconnectTimer = null;
  }
  debug({ message: 'Health check monitoring cleanup complete', labels: [LogLabel.PUBNUB_RECOVERY] });
}
